// pages/components/billing/UsageStats.jsx
import React from 'react';
import { Briefcase, Send, TrendingUp, Target } from 'lucide-react';

const UsageStats = ({ usageStats }) => {
if (!usageStats) return null;

const {
package: currentPackage,
jobsLimit,
matchesThisMonth,
remainingMatches,
applicationsThisMonth,
usagePercentage
} = usageStats;

const getProgressColor = (percentage) => {
if (percentage >= 90) return 'bg-red-500';
if (percentage >= 70) return 'bg-amber-500';
return 'bg-green-500';
};

const stats = [
{
label: 'Job Matches',
value: matchesThisMonth,
limit: jobsLimit === -1 ? '∞' : jobsLimit,
icon: Briefcase,
color: 'from-blue-400 to-blue-600',
bgColor: 'bg-blue-50',
iconBg: 'bg-blue-100',
iconColor: 'text-blue-600'
},
{
label: 'Applications Sent',
value: applicationsThisMonth,
icon: Send,
color: 'from-purple-400 to-purple-600',
bgColor: 'bg-purple-50',
iconBg: 'bg-purple-100',
iconColor: 'text-purple-600'
},
{
label: 'Remaining Matches',
value: remainingMatches === 'unlimited' ? '∞' : remainingMatches,
icon: Target,
color: 'from-green-400 to-green-600',
bgColor: 'bg-green-50',
iconBg: 'bg-green-100',
iconColor: 'text-green-600'
}
];

return (
<div className="space-y-6">
<h2 className="text-lg lg:text-xl font-bold text-slate-900">Usage This Month</h2>

{/* Stats Grid */}
<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
{stats.map((stat, idx) => {
const Icon = stat.icon;
return (
<div
key={idx}
className={`${stat.bgColor} rounded-2xl p-6 border border-slate-200 flex items-center gap-3 shadow-sm hover:shadow-md transition-all`}
>
<div className="flex items-start justify-between mb-4">
<div className={`${stat.iconBg} p-3 rounded-xl`}>
<Icon className={stat.iconColor} size={16} />
</div>
</div>

<div>
<div className="flex items-baseline gap-2">
<p className="text-3xl font-bold text-slate-900">{stat.value}</p>
{stat.limit && (
<p className="text-slate-500 text-lg">/ {stat.limit}</p>
)}
</div>

<p className="text-slate-600 text-sm font-medium mb-2">{stat.label}</p>
</div>

</div>
);
})}
</div>

{/* Progress Bar for Job Matches */}
{jobsLimit !== -1 && (
<div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
<div className="flex items-center justify-between mb-3">
<h3 className="font-bold text-slate-900">Monthly Job Match Quota</h3>
<span className="text-sm font-semibold text-slate-600">
{usagePercentage}% Used
</span>
</div>

<div className="w-full bg-slate-200 rounded-full h-4 overflow-hidden">
<div
className={`h-full ${getProgressColor(usagePercentage)} transition-all duration-500 rounded-full relative overflow-hidden`}
style={{ width: `${Math.min(usagePercentage, 100)}%` }}
>
<div className="absolute inset-0 bg-white/20 animate-pulse"></div>
</div>
</div>

<div className="mt-4 flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
<TrendingUp className="text-indigo-600 flex-shrink-0 mt-0.5" size={20} />
<div>
<p className="text-sm font-semibold text-slate-900 mb-1">
Want more job matches?
</p>
<p className="text-xs text-slate-600">
Upgrade your plan to get more monthly job matches and unlock premium features.
</p>
</div>
</div>
</div>
)}

{/* Monthly Reset Info */}
<div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
<div className="flex items-start gap-3">
<div className="p-2 bg-indigo-100 rounded-lg">
<TrendingUp className="text-indigo-600" size={20} />
</div>
<div>
<h3 className="font-bold text-slate-900 mb-1">Usage Resets Monthly</h3>
<p className="text-sm text-slate-600">
Your job match quota resets on the 1st of each month. Applications sent don't count against your quota.
</p>
</div>
</div>
</div>
</div>
);
};

export default UsageStats;